import axios from 'axios';
import api from '@/lib/axios';
import type { School, SchoolOwner, SetOwnerPayload, SetOwnerResult } from './superadmin.api';
import type { DataTableParams, PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

// ───── Types ────────────────────────────────────────────────────────────────
export interface OrgMe {
  id: string;
  name: string;
  adminName: string | null;
  adminEmail: string;
  adminPhone: string | null;
  maxSchoolsAllowed: number;
  status: 'active' | 'inactive';
  schoolsUsed: number;
}

export interface OrgSchool {
  id: string;
  name: string;
  code: string;
  slug: string;
  supportEmail: string;
  status: 'trial' | 'active' | 'grace_period' | 'suspended' | 'cancelled';
  createdAt: string;
  adminName?: string | null;
  adminEmail?: string | null;
  /** Raw legacy state id — no name lookup exists, see backend ListSchoolsDto. */
  stateId?: number | null;
}

export interface ListOrgSchoolsParams extends Partial<DataTableParams> {
  status?: OrgSchool['status'];
  stateId?: number;
}

export interface OrgGrant {
  id: string;
  role: string;
  status: 'active' | 'revoked';
  createdAt: string;
  userAccount?: { id: string; name: string; email: string } | null;
}

export interface CreateOrgSchoolPayload {
  name: string;
  code?: string;
  supportEmail: string;
  supportPhone?: string;
  status?: OrgSchool['status'];
  ownerName?: string;
  ownerEmail?: string;
  ownerPassword?: string;
}

export interface CreateOrgGrantPayload {
  name: string;
  email: string;
  password?: string;
  role: string;
}

// ───── Org admin auth ─────────────────────────────────────────────────────────
export interface OrgLoginResult {
  admin: {
    id: string;
    name: string;
    email: string | null;
    mobile: string | null;
    role: 'superadmin' | 'org_admin' | 'support';
    organizationId: string;
    mustChangePassword: boolean;
    scope: 'organization';
  };
  organization: {
    id: string;
    name: string;
    maxSchoolsAllowed: number;
    status: string;
  };
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

/** 'school'-scoped session returned by switch-school (mirrors school login). */
export interface TenantSession {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    schoolId: string;
    schoolSlug: string;
  };
  school: { id: string; slug: string; status: string; isSchemaProvisioned: boolean };
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

export const OrgAuthApi = {
  // Raw axios (no tenant header, no stale-token refresh) for login.
  login: async (login: string, password: string): Promise<OrgLoginResult> => {
    const r = await axios.post('/api/v1/auth/organization/login', {
      login,
      password,
    });
    return (r.data?.data ?? r.data) as OrgLoginResult;
  },
  // Schools this org admin may enter — paginated/searchable, master DB only.
  // Requires the org token explicitly (same reasoning as switchSchool below).
  myOrgSchools: async (
    params: DataTableParams,
    orgToken: string,
  ): Promise<PaginatedResult<School>> => {
    const r = await axios.get('/api/v1/auth/organization/my-schools', {
      params,
      headers: { Authorization: `Bearer ${orgToken}` },
    });
    return (r.data?.data ?? r.data) as PaginatedResult<School>;
  },
  // Raw axios with the explicit org token — after entering a school the active
  // token becomes a school token, so switching must always present the
  // original org token, never the current school token.
  switchSchool: async (
    schoolId: string,
    orgToken: string,
  ): Promise<TenantSession> => {
    const r = await axios.post(
      '/api/v1/auth/organization/switch-school',
      { schoolId },
      { headers: { Authorization: `Bearer ${orgToken}` } },
    );
    return (r.data?.data ?? r.data) as TenantSession;
  },
  // Routed through `api` (NOT raw axios) deliberately — the target token
  // here (the current school-acting session) is exactly what `api`'s
  // request interceptor already auto-attaches from the store, and critically
  // its response interceptor gives this call the same silent
  // refresh-and-retry-once-on-401 treatment every other authenticated call
  // in the app gets. Raw axios had NO retry at all: if the access token
  // happened to be expired at the exact moment "Back to organization" was
  // clicked, this failed outright with a bare 401 instead of transparently
  // refreshing first — the reported "switching back to org logs me out"
  // bug. `refreshOrgActingSession` (auth.service.ts) already handles
  // refreshing this exact token shape correctly; this was the one call
  // that never gave it the chance to.
  exitSchool: async (): Promise<OrgLoginResult> => {
    const r = await api.post('/auth/organization/exit-school', {});
    return (r.data?.data ?? r.data) as OrgLoginResult;
  },
};

// ───── Org portal (scoped to the org token) ──────────────────────────────────
export const OrgPortalApi = {
  me: async (): Promise<OrgMe> => unwrap(await api.get('/org/me')),
  /** Paginated — {items, total, page, limit, totalPages}. Pass {limit: 100} for an "all schools" dropdown/count use. */
  listSchools: async (params?: ListOrgSchoolsParams): Promise<PaginatedResult<OrgSchool>> =>
    unwrap(await api.get('/org/schools', { params })),
  listSchoolStates: async (): Promise<{ id: number; name: string | null }[]> =>
    unwrap(await api.get('/org/schools/states')),
  createSchool: async (payload: CreateOrgSchoolPayload): Promise<OrgSchool> =>
    unwrap(await api.post('/org/schools', payload)),
  removeSchool: async (id: string) =>
    unwrap(await api.delete(`/org/schools/${id}`)),
  updateSchoolStatus: async (id: string, status: 'active' | 'suspended'): Promise<OrgSchool> =>
    unwrap(await api.patch(`/org/schools/${id}/status`, { status })),
  listGrants: async (schoolId: string): Promise<OrgGrant[]> =>
    unwrap(await api.get(`/org/schools/${schoolId}/grants`)),
  grant: async (
    schoolId: string,
    payload: CreateOrgGrantPayload,
  ): Promise<OrgGrant> =>
    unwrap(await api.post(`/org/schools/${schoolId}/grants`, payload)),
  revokeGrant: async (grantId: string) =>
    unwrap(await api.delete(`/org/grants/${grantId}`)),
  getOwner: async (schoolId: string): Promise<SchoolOwner> =>
    unwrap(await api.get(`/org/schools/${schoolId}/owner`)),
  setOwner: async (schoolId: string, payload: SetOwnerPayload): Promise<SetOwnerResult> =>
    unwrap(await api.put(`/org/schools/${schoolId}/owner`, payload)),
};

// ───── Org dashboard (stats, students-per-class, attendance) ────────────────
export interface OrgDashboardOverview {
  schools: { total: number; active: number };
  students: { total: number };
  teachers: { total: number };
  studentsPerClass: { className: string; count: number }[];
  leastAttendanceMarkedSchools: {
    schoolId: string;
    schoolName: string;
    lastMarkedDate: string | null;
    totalStudents: number;
    markedCount: number;
    presentCount: number;
    markedPct: number;
  }[];
  fees: { available: false };
  defaultPeriod: { month: number; year: number } | null;
}

export interface OrgAttendanceTrendPoint {
  date: string;
  presentCount: number;
  totalCount: number;
  presentPct: number;
}

export interface OrgAttendanceTrend {
  month: number;
  year: number;
  students: OrgAttendanceTrendPoint[];
  teachers: OrgAttendanceTrendPoint[];
}

export const OrgDashboardApi = {
  overview: async (): Promise<OrgDashboardOverview> => unwrap(await api.get('/org/dashboard/overview')),
  attendanceTrend: async (month?: number, year?: number): Promise<OrgAttendanceTrend> =>
    unwrap(await api.get('/org/dashboard/attendance-trend', { params: { month, year } })),
};
